import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editSuspectToWitness } from "services/caseServices";
import { SuspectTowitness } from "types";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  conversion: SuspectTowitness;
  onClose: (refetch: boolean) => void;
};

const RevokeRequest: FC<Props> = ({ conversion, onClose }) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<SuspectTowitness>(conversion);

  useEffect(() => {
    if (conversion) {
      setFormState(conversion);
    }
  }, [conversion]);

  const validateForm = () => {
    if (!formState.revocation_reason) return t("stw_revoke_reason_required");
    if (!formState.revocation_summary) return t("stw_revoke_summary_required");
    if (!formState.revocation_file) return t("stw_revoke_file_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    editSuspectToWitness({
      ...formState,
      status: "forwarded_to_cordinator",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (name: string, file: File | null) => {
    setFormState({ ...formState, [name]: file });
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("stw_revoke_request_title")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("lorem_ipsum_placeholder")}
        </p>

        <form onSubmit={handleSubmit}>
          <div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("stw_revoke_reason")}
                </label>
                <textarea
                  rows={4}
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="revocation_reason"
                  value={formState?.revocation_reason ?? ""}
                  onChange={(e) => {
                    setFormState({
                      ...formState,
                      revocation_reason: e.target.value,
                    });
                  }}
                  // disabled
                />
              </div>
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("stw_revoke_summary")}
                </label>
                <textarea
                  rows={4}
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="revocation_summary"
                  value={formState?.revocation_summary ?? ""}
                  onChange={(e) => {
                    setFormState({
                      ...formState,
                      revocation_summary: e.target.value,
                    });
                  }}
                  // disabled
                />
              </div>
              <div className="flex flex-col mt-4 col-span-2">
                <FileUpload
                  label={t("stw_revoke_file")}
                  fileTypes="*"
                  required={true}
                  onChange={(file) => handleFile("revocation_file", file)}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-row mt-4 gap-3">
            <input
              type="submit"
              className="border border-[#212121] bg-[#212121] px-[76px] py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
              value={t("stw_revoke_request")}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default RevokeRequest;
